import { z } from "zod";

export const planKeySchema = z.enum(["free", "pro", "business"]);
export type PlanKey = z.infer<typeof planKeySchema>;

export type AnalyticsTier = "basic" | "detailed" | "advanced";
export type QrTier = "standard" | "custom" | "table";
export type EditorTier = "basic" | "full";

export type PlanLimits = {
  key: PlanKey;
  name: string;
  /** Monthly price in USD cents (initial hypothesis, see spec §2). */
  priceUsdCents: number;
  /** Total menus (draft or published) the organization may keep. */
  maxMenus: number;
  /** Menus that may be published simultaneously. */
  maxPublishedMenus: number;
  maxRestaurants: number;
  /** Theme keys allowed, or "all". */
  themes: "all" | string[];
  editor: EditorTier;
  /** Additional languages beyond the default one. */
  maxAdditionalLanguages: number;
  aiTranslation: boolean;
  imports: boolean;
  /** Monthly AI credits (translation + import). */
  monthlyCredits: number;
  qr: QrTier;
  analytics: AnalyticsTier;
  /** Days of raw analytics retained / visible. */
  analyticsRetentionDays: number;
  tableOrdering: boolean;
  customDomain: boolean;
  maxMembers: number;
  maxTables: number;
  storageBytes: number;
  maxImportPdfBytes: number;
  maxImportPdfPages: number;
  maxImportWebsitePages: number;
  maxConcurrentJobs: number;
};

const MB = 1024 * 1024;

export const PLANS: Record<PlanKey, PlanLimits> = {
  free: {
    key: "free",
    name: "Free",
    priceUsdCents: 0,
    maxMenus: 1,
    maxPublishedMenus: 1,
    maxRestaurants: 1,
    themes: ["basic"],
    editor: "basic",
    maxAdditionalLanguages: 1,
    aiTranslation: false,
    imports: false,
    monthlyCredits: 0,
    qr: "standard",
    analytics: "basic",
    analyticsRetentionDays: 30,
    tableOrdering: false,
    customDomain: false,
    maxMembers: 1,
    maxTables: 0,
    storageBytes: 50 * MB,
    maxImportPdfBytes: 0,
    maxImportPdfPages: 0,
    maxImportWebsitePages: 0,
    maxConcurrentJobs: 1,
  },
  pro: {
    key: "pro",
    name: "Pro",
    priceUsdCents: 1200,
    maxMenus: 5,
    maxPublishedMenus: 5,
    maxRestaurants: 1,
    themes: "all",
    editor: "full",
    maxAdditionalLanguages: 30,
    aiTranslation: true,
    imports: true,
    monthlyCredits: 500,
    qr: "custom",
    analytics: "detailed",
    analyticsRetentionDays: 180,
    tableOrdering: false,
    customDomain: false,
    maxMembers: 2,
    maxTables: 0,
    storageBytes: 1024 * MB,
    maxImportPdfBytes: 15 * MB,
    maxImportPdfPages: 30,
    maxImportWebsitePages: 10,
    maxConcurrentJobs: 2,
  },
  business: {
    key: "business",
    name: "Business",
    priceUsdCents: 2900,
    // "Unlimited under fair use"
    maxMenus: 100,
    maxPublishedMenus: 100,
    maxRestaurants: 10,
    themes: "all",
    editor: "full",
    maxAdditionalLanguages: 30,
    aiTranslation: true,
    imports: true,
    monthlyCredits: 2000,
    qr: "table",
    analytics: "advanced",
    analyticsRetentionDays: 395,
    tableOrdering: true,
    customDomain: true,
    maxMembers: 10,
    maxTables: 200,
    storageBytes: 5 * 1024 * MB,
    maxImportPdfBytes: 25 * MB,
    maxImportPdfPages: 60,
    maxImportWebsitePages: 25,
    maxConcurrentJobs: 4,
  },
};

export const CREDIT_COSTS = {
  translateItem: 1,
  analyzePdfPage: 5,
  pdfImport: 25,
  websiteImport: 50,
  regenerateDesign: 20,
  floorPlan: 5,
} as const;

export function planAllowsTheme(plan: PlanLimits, themeKey: string): boolean {
  return plan.themes === "all" || plan.themes.includes(themeKey);
}
